import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { haptic } from "../lib/telegram.js";
import { sound } from "../lib/sound.js";
import { randomFleet, validateFleet, FLEET_SPEC, SIZE } from "../lib/fleet.js";
import type { SeaMove, SeaView } from "../lib/views.js";

interface Props {
  view: SeaView;
  onMove(move: SeaMove): void;
}

export function SeaBattle({ view, onMove }: Props) {
  return view.phase === "placing" ? (
    <Placing view={view} onMove={onMove} />
  ) : (
    <Battle view={view} onMove={onMove} />
  );
}

// ─────────────────────────── расстановка ───────────────────────────

function Placing({ view, onMove }: Props) {
  const [fleet, setFleet] = useState<number[][]>(() => randomFleet());
  const [sent, setSent] = useState(view.ready.mine);

  useEffect(() => {
    if (view.ready.mine) setSent(true);
  }, [view.ready.mine]);

  const occupied = useMemo(() => new Set(fleet.flat()), [fleet]);

  const reroll = () => {
    haptic.tap();
    sound.select();
    setFleet(randomFleet());
  };

  const confirm = () => {
    haptic.move();
    setSent(true);
    onMove({ type: "place", ships: fleet });
  };

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="sea-title">
        {sent ? "Ждём соперника" : "Расставь флот"}
        <span className="muted small">
          {sent
            ? view.ready.theirs
              ? "начинаем"
              : "он ещё расставляет корабли"
            : "жми «Другая расстановка», пока не понравится"}
        </span>
      </div>

      <SeaGrid
        cells={Array.from({ length: SIZE * SIZE }, (_, i) => ({
          ship: occupied.has(i),
        }))}
        onCell={() => {}}
      />

      {!sent && (
        <>
          <div className="sea-fleet muted small">
            Флот: {FLEET_SPEC.join(" · ")} — корабли не касаются друг друга
          </div>
          <button className="btn" onClick={reroll}>
            Другая расстановка
          </button>
          <button className="btn btn-primary" onClick={confirm}>
            Я готов
          </button>
        </>
      )}

      {sent && !view.ready.theirs && (
        <div className="center" style={{ padding: 12 }}>
          <div className="spinner" />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── бой ───────────────────────────

function Battle({ view, onMove }: Props) {
  const myTurn = view.turn === view.me;

  const shoot = (cell: number) => {
    if (!myTurn) return;
    if (view.outgoing[cell] !== undefined) return;
    haptic.move();
    onMove({ type: "shoot", cell });
  };

  // Своё поле: корабли, попадания соперника и его промахи.
  const myShipCells = useMemo(() => {
    const hit = new Set<number>();
    const ship = new Set<number>();
    const sunk = new Set<number>();
    for (const s of view.myShips) {
      for (const c of s.cells) {
        ship.add(c);
        if (s.sunk) sunk.add(c);
      }
      for (const c of s.hits) hit.add(c);
    }
    return { ship, hit, sunk };
  }, [view.myShips]);

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="sea-title">
        {myTurn ? "Твой выстрел" : "Стреляет соперник"}
        <span className="muted small">
          Кораблей: у тебя {view.shipsLeft.mine}, у соперника {view.shipsLeft.theirs}
        </span>
      </div>

      {/* Поле соперника — по нему стреляем */}
      <div className="sea-label">Поле соперника</div>
      <SeaGrid
        cells={Array.from({ length: SIZE * SIZE }, (_, i) => {
          const mark = view.outgoing[i];
          return {
            miss: mark === "miss",
            hit: mark === "hit",
            sunk: mark === "sunk",
            playable: myTurn && mark === undefined,
            last: view.lastShot?.by === view.me && view.lastShot.cell === i,
          };
        })}
        onCell={shoot}
      />

      {/* Своё поле — только смотрим */}
      <div className="sea-label">Твоё поле</div>
      <SeaGrid
        small
        cells={Array.from({ length: SIZE * SIZE }, (_, i) => {
          const mark = view.incoming[i];
          return {
            ship: myShipCells.ship.has(i) && !myShipCells.hit.has(i),
            hit: myShipCells.hit.has(i) && !myShipCells.sunk.has(i),
            sunk: myShipCells.sunk.has(i),
            miss: mark === "miss",
            last: view.lastShot?.by === view.opponent && view.lastShot.cell === i,
          };
        })}
        onCell={() => {}}
      />
    </div>
  );
}

// ─────────────────────────── сетка ───────────────────────────

interface SeaCell {
  ship?: boolean;
  hit?: boolean;
  sunk?: boolean;
  miss?: boolean;
  playable?: boolean;
  last?: boolean;
}

function SeaGrid({
  cells,
  onCell,
  small,
}: {
  cells: SeaCell[];
  onCell(index: number): void;
  small?: boolean;
}) {
  return (
    <div className={`sea-grid${small ? " is-small" : ""}`}>
      {cells.map((c, i) => {
        const cls = [
          "sea-cell",
          c.ship ? "is-ship" : "",
          c.hit ? "is-hit" : "",
          c.sunk ? "is-sunk" : "",
          c.miss ? "is-miss" : "",
          c.playable ? "is-playable" : "",
          c.last ? "is-last" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div key={i} className={cls} onClick={() => onCell(i)}>
            {(c.hit || c.sunk) && (
              <motion.span
                className="sea-mark"
                initial={{ scale: 0.3, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 520, damping: 26 }}
              >
                ✕
              </motion.span>
            )}
            {c.miss && <span className="sea-dot" />}
          </div>
        );
      })}
    </div>
  );
}

export { validateFleet };
